import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ReportConfiguration } from 'app-components/flux/report-configurations/reportConfigurationsTypes';

import reportsAdapter from './generatedReportsEntityAdapter';
import {
  GENERATED_REPORTS_REDUCER_KEY,
  type ReconcileReportTransactionsPayload,
  type ReconcileReportTransactionsSuccessPayload,
  type Report,
} from './generatedReportsTypes';

const slice = createSlice({
  name: GENERATED_REPORTS_REDUCER_KEY,
  initialState: () => reportsAdapter.getInitialState({ activeViewingConfigId: null as string | null, dataVersion: 0 }),
  reducers: {
    incrementDataVersion(state) {
      state.dataVersion += 1;
    },
    generateReport(
      state,
      action: PayloadAction<{
        location: string;
        configuration: ReportConfiguration;
        requestedAtVersion?: number;
      }>,
    ) {
      const { payload } = action;
      // Create state for the first request and preserve any cached report during refresh.
      reportsAdapter.upsertOne(state, { location: payload.location, isGenerating: true, error: undefined });
    },
    generateReportSuccess(
      state,
      action: PayloadAction<{ location: string; report: Report; requestedAtVersion?: number }>,
    ) {
      const { payload } = action;
      if (payload.requestedAtVersion !== undefined && payload.requestedAtVersion !== state.dataVersion) return;

      const previous = reportsAdapter.getSelectors().selectById(state, payload.location);
      reportsAdapter.setOne(state, {
        location: payload.location,
        report: payload.report,
        isGenerating: false,
        generatedAtVersion: state.dataVersion,
        generation: (previous?.generation ?? 0) + 1,
      });
    },
    generateReportFailure(
      state,
      action: PayloadAction<{ location: string; error: Error; requestedAtVersion?: number }>,
    ) {
      const { payload } = action;
      if (payload.requestedAtVersion !== undefined && payload.requestedAtVersion !== state.dataVersion) return;

      reportsAdapter.upsertOne(state, {
        location: payload.location,
        error: payload.error,
        isGenerating: false,
      });
    },
    setActiveViewingConfig(state, action: PayloadAction<string>) {
      state.activeViewingConfigId = action.payload;
    },
    clearActiveViewingConfig(state) {
      state.activeViewingConfigId = null;
    },
    reconcileReportTransactions(state, action: PayloadAction<ReconcileReportTransactionsPayload>) {
      const { attemptKey, generation, location, transactionIds } = action.payload;
      const entity = reportsAdapter.getSelectors().selectById(state, location);
      if (!entity || entity.generation !== generation || entity.transactionReconciliations?.[attemptKey]) {
        return;
      }
      reportsAdapter.updateOne(state, {
        id: location,
        changes: {
          transactionReconciliations: {
            ...entity.transactionReconciliations,
            [attemptKey]: {
              generation,
              status: 'loading',
              requestedIds: transactionIds,
              unresolvedIds: transactionIds,
              outcomes: [],
            },
          },
        },
      });
    },
    reconcileReportTransactionsSuccess(state, action: PayloadAction<ReconcileReportTransactionsSuccessPayload>) {
      const { attemptKey, generation, location, outcomes, transactionIds } = action.payload;
      const entity = reportsAdapter.getSelectors().selectById(state, location);
      if (entity?.generation !== generation || !entity.transactionReconciliations?.[attemptKey]) {
        return;
      }
      const unresolvedIds = outcomes.filter(({ status }) => status !== 'found').map(({ id }) => id);
      reportsAdapter.updateOne(state, {
        id: location,
        changes: {
          transactionReconciliations: {
            ...entity.transactionReconciliations,
            [attemptKey]: {
              generation,
              status: unresolvedIds.length === 0 ? 'complete' : 'partial',
              requestedIds: transactionIds,
              unresolvedIds,
              outcomes,
            },
          },
        },
      });
    },
    reconcileReportTransactionsFailure(
      state,
      action: PayloadAction<ReconcileReportTransactionsPayload & { error: string }>,
    ) {
      const { attemptKey, error, generation, location, transactionIds } = action.payload;
      const entity = reportsAdapter.getSelectors().selectById(state, location);
      if (entity?.generation !== generation || !entity.transactionReconciliations?.[attemptKey]) {
        return;
      }
      reportsAdapter.updateOne(state, {
        id: location,
        changes: {
          transactionReconciliations: {
            ...entity.transactionReconciliations,
            [attemptKey]: {
              generation,
              status: 'failed',
              requestedIds: transactionIds,
              unresolvedIds: transactionIds,
              outcomes: [],
              error,
            },
          },
        },
      });
    },
  },
});

export const generatedReportsActions = slice.actions;
export default slice.reducer;
